import { Component, ViewEncapsulation, OnInit, EventEmitter, Output, Inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { AlertResponse, AlertIdentificators, AlertSelectParams } from '@shared/widgets/entities/alerts';
import { UserAlertsFacade } from '@user-alerts/store/user-alerts.facade';
import { flightResultsUrlMapper } from '@user-alerts/mappers/flight-results-url.mapper';
import { flightResultsRootRoute } from '@flight-results/flight-results-routing.constants';
import { environment as ENV } from '@environment';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { UserAlertsAbstract } from '@user-alerts/abstracts/user-alerts.abstract';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { FlightType } from '@search-bar/entities/flight-type';
import { invertAlertDateBoundFormat } from '@shared/widgets/mappers/alert-date-format.mapper';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { userAlertsRoute, createAlertRoute } from '@user-alerts/constants/user-alerts-routes.constants';
import { AppFacade } from '@app/app.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { BackgroundSurfaceType } from '@design-system/components/atoms/background-surface/entities/background-surface.types';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-user-alerts',
  templateUrl: './user-alerts.container.html',
  styleUrls: ['./user-alerts.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class UserAlertsContainer extends UserAlertsAbstract implements OnInit, OnDestroy {

  @Output() toggleDesktopAlertsManagement: EventEmitter<void> = new EventEmitter<void>();
  @Output() confirmDeleteAlertDesktop: EventEmitter<AlertIdentificators> = new EventEmitter<AlertIdentificators>();
  @Output() updateAlert: EventEmitter<AlertResponse> = new EventEmitter<AlertResponse>();

  public alertsEmptyImg: string = 'alerts-megaphone.png';
  public showSwipeUp: boolean = false;
  public placeholderQuantity: number = 3;

  private readonly source = EVENT_SOURCE.TAG;
  private readonly subscriptions: Subscription = new Subscription();

  public readonly surface = BackgroundSurfaceType.Primary;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly domManipulateService: DomManipulateService,
    private readonly router: Router,
    private readonly appFacade: AppFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    protected amplitude: PropertiesAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected userAlertsFacade: UserAlertsFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    super(userAlertsFacade, amplitude, triggerAmplitudeService);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.userAlertsFacade.resetWidgetData();
      this.subscriptions.add(
        this.isMobile$.subscribe(isMobile => {
          if (!isMobile) {
            this.router.navigate(['/']);
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get alertsEntities$(): Observable<AlertResponse[]> {
    return this.userAlertsFacade.alertsEntities$;
  }

  get hasAlertsEntities$(): Observable<boolean> {
    return combineLatest(
      [this.userAlertsFacade.alertsActiveEntites$,
        this.isLoadingAlertList$]
    ).pipe(map(([alertsEntities, isLoadingAlertList]) =>
      !!alertsEntities && alertsEntities.length > 0 &&
      !isLoadingAlertList));
  }

  get isLoadingAlertList$(): Observable<boolean> {
    return this.userAlertsFacade.isLoadingAlertList$;
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$.pipe(map(isMobileDevice => isMobileDevice));
  }

  get flightType(): FlightType {
    return this.alertSelected.oneway ? FlightType.ONEWAY : FlightType.ROUNDTRIP;
  }

  get alertSelectedDepartureDate(): Date {
    return this.alertSelected ?
      formatDateByRegex(invertAlertDateBoundFormat(this.alertSelected.outbound_min, !!this.alertSelected.flex_dates)) : null;
  }

  get alertSelectedArrivalDate(): Date {
    return this.alertSelected ?
      !this.alertSelected.oneway ?
        formatDateByRegex(invertAlertDateBoundFormat(this.alertSelected.inbound_max, !!this.alertSelected.flex_dates, true))
        : null : null;
  }

  public trackBy(index: number, item: AlertResponse): number {
    return item.id;
  }

  public removeAlert(alertIdentificator: AlertIdentificators): void {
    this.isMobile$.pipe(
      take(1),
      tap(isMobile => {
        if (isMobile) {
          this.userAlertsFacade.deleteAlert(alertIdentificator);
        } else {
          this.confirmDeleteAlertDesktop.emit(alertIdentificator);
        }
      })
    ).subscribe().unsubscribe();
  }

  public editAlert(alert: AlertResponse): void {
    this.alertSelected = alert;
    this.toggleConfirmForm();
  }

  public toggleConfirmForm(): void {
    this.isMobile$.pipe(
      take(1),
      tap(isMobile => {
        if (isMobile) {
          this.showSwipeUp = !this.showSwipeUp;
          this.domManipulateService.setBodyScroll(!this.showSwipeUp);
        } else {
          this.updateAlert.emit(this.alertSelected);
        }
      })
    ).subscribe().unsubscribe();
    
  }

  public createAlertEvent(params: AlertSelectParams): void {
    this.createAlert(params);
    this.toggleConfirmForm();
    this.toggleAlertCreated(!this.hasAlertSelection);
    this.alertSelected = null;
  }

  public openFlights(alert: AlertResponse): void {
    const urlParams = flightResultsUrlMapper(alert);
    this.userAlertsFacade.setActiveAlertsUI(true);
    this.userAlertsFacade.loadAlertSuccess(alert);
    this.toggleDesktopAlertsManagement.emit();
    this.appFacade.historyReset();
    this.router.navigate([`${flightResultsRootRoute}/${urlParams}`], {
      queryParams: { source: this.source, branch_code: ENV.branchcode }
    });
  }

  public onRedirectCreate(): void {
    this.router.navigateByUrl(`${userAlertsRoute}/${createAlertRoute}`);
  }

}
